import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api.js';
import { usePeriodStore } from '@/stores/period.js';
import { PageHeader, Section } from '@/components/PageHeader.jsx';
import { Button } from '@/components/ui/Button.jsx';
import { Input, DateInput, FormField, Textarea } from '@/components/ui/Input.jsx';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components/ui/Dialog.jsx';
import { Badge } from '@/components/ui/Badge.jsx';
import { DataTable } from '@/components/DataTable.jsx';
import { StatCard } from '@/components/StatCard.jsx';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs.jsx';
import { EmployeePicker } from '@/components/EmployeePicker.jsx';
import { useToast } from '@/components/ui/Toast.jsx';
import { formatDate, monthName, formatLKR } from '@/lib/format.js';
import { Plus, Check, Trash2, Clock, Zap } from 'lucide-react';

export default function OvertimePage() {
  const { year, month } = usePeriodStore();
  const qc = useQueryClient();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({ employee_id: '', date: new Date().toISOString().slice(0, 10), hours: 0, rate_multiplier: 1.5, reason: '' });

  const queryKey = ['overtime', { year, month }];
  const { data = [], isLoading } = useQuery({ queryKey, queryFn: () => api.get(`/overtime?year=${year}&month=${month}`) });

  const autoQueryKey = ['overtimeAuto', { year, month }];
  const { data: autoData = [], isLoading: autoLoading } = useQuery({
    queryKey: autoQueryKey,
    queryFn: () => api.get(`/overtime/auto?year=${year}&month=${month}`),
  });

  const create = useMutation({
    mutationFn: () => api.post('/overtime', {
      employee_id: Number(draft.employee_id), date: draft.date,
      hours: Number(draft.hours), rate_multiplier: Number(draft.rate_multiplier),
      approved: false, reason: draft.reason || null,
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey }); setOpen(false); toast({ variant: 'success', title: 'Created' }); },
    onError: (e) => toast({ variant: 'error', title: 'Failed', description: e.message }),
  });
  const approve = useMutation({ mutationFn: (id) => api.post(`/overtime/${id}/approve`), onSuccess: () => qc.invalidateQueries({ queryKey }) });
  const del = useMutation({ mutationFn: (id) => api.del(`/overtime/${id}`), onSuccess: () => qc.invalidateQueries({ queryKey }) });

  const SOURCE_LABEL = { weekday: 'Weekday', saturday: 'Saturday', sunday: 'Sunday', holiday: 'Holiday' };
  const hoursToHHMM = (h) => { const t = Math.round(Number(h) * 60); return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, '0')}`; };

  const manualColumns = [
    { key: 'date', header: 'Date', sortable: true, render: (r) => formatDate(r.date), className: 'font-mono' },
    { key: 'emp_no', header: 'Emp No', className: 'font-mono', sortable: true },
    { key: 'full_name', header: 'Name', sortable: true },
    { key: 'hours', header: 'Hours', render: (r) => hoursToHHMM(r.hours), className: 'text-right tabular-nums font-mono', headClassName: 'text-right' },
    { key: 'rate_multiplier', header: 'Rate ×', className: 'font-mono' },
    { key: 'reason', header: 'Reason', className: 'font-mono' },
    { key: 'approved', header: 'Status', render: (r) => <Badge variant={r.approved ? 'success' : 'warning'}>{r.approved ? 'approved' : 'pending'}</Badge> },
    { key: '__a', header: '', className: 'w-24 text-right',
      render: (r) => (
        <div className="flex justify-end gap-1">
          {!r.approved && <Button variant="ghost" size="icon" onClick={() => approve.mutate(r.id)}><Check className="h-4 w-4 text-emerald-600" /></Button>}
          <Button variant="ghost" size="icon" onClick={() => del.mutate(r.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
        </div>
      ) },
  ];

  const autoColumns = [
    { key: 'date', header: 'Date', sortable: true, render: (r) => formatDate(r.date), className: 'font-mono' },
    { key: 'emp_no', header: 'Emp No', className: 'font-mono', sortable: true },
    { key: 'full_name', header: 'Name', sortable: true },
    { key: 'source', header: 'Source', render: (r) => <Badge variant="info">{SOURCE_LABEL[r.source] ?? r.source}</Badge> },
    { key: 'hours', header: 'Hours', accessor: (r) => r.hours, render: (r) => hoursToHHMM(r.hours), className: 'text-right tabular-nums font-mono', headClassName: 'text-right' },
    { key: 'multiplier', header: 'Rate ×', render: (r) => `× ${Number(r.multiplier).toFixed(1)}`, className: 'font-mono' },
    { key: 'amount_cents', header: 'Amount', accessor: (r) => r.amount_cents, render: (r) => formatLKR(r.amount_cents), className: 'text-right tabular-nums font-mono', headClassName: 'text-right' },
  ];

  const autoTotalHours  = autoData.reduce((s, r) => s + Number(r.hours || 0), 0);
  const autoTotalAmount = autoData.reduce((s, r) => s + Number(r.amount_cents || 0), 0);

  return (
    <div className="space-y-5">
      <PageHeader title="Overtime" description={`${monthName(month)} ${year}`}
        actions={<Button onClick={() => setOpen(true)}><Plus className="h-4 w-4" />Record OT</Button>}
      />

      <Tabs defaultValue="auto">
        <TabsList>
          <TabsTrigger value="manual"><Clock className="h-4 w-4 mr-1" />Manual OT</TabsTrigger>
          <TabsTrigger value="auto"><Zap className="h-4 w-4 mr-1" />Auto OT</TabsTrigger>
        </TabsList>

        <TabsContent value="manual">
          <Section>
            <DataTable columns={manualColumns} data={data} loading={isLoading} exportFilename={`overtime-manual-${year}-${month}.csv`} />
          </Section>
        </TabsContent>

        <TabsContent value="auto">
          <div className="grid gap-4 md:grid-cols-2 mb-4">
            <StatCard label="Total Auto Hours" value={hoursToHHMM(autoTotalHours)} icon={Clock} accent="info" />
            <StatCard label="Total Auto Amount" value={formatLKR(autoTotalAmount)} icon={Zap} accent="success" />
          </div>
          <Section>
            <p className="text-sm text-muted-foreground mb-3">
              Auto OT is derived from attendance punch times versus shift schedules. To override a specific day, add a manual OT record — it will suppress auto-calculation for that date.
            </p>
            <DataTable columns={autoColumns} data={autoData} loading={autoLoading} exportFilename={`overtime-auto-${year}-${month}.csv`} />
          </Section>
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Record Overtime</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <FormField label="Employee" required><EmployeePicker value={draft.employee_id} onChange={(v) => setDraft({ ...draft, employee_id: v })} /></FormField>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Date" required><DateInput value={draft.date} onChange={(e) => setDraft({ ...draft, date: e.target.value })} /></FormField>
              <FormField label="Hours" required><Input type="number" step="0.25" value={draft.hours} onChange={(e) => setDraft({ ...draft, hours: e.target.value })} /></FormField>
              <FormField label="Rate Multiplier"><Input type="number" step="0.1" value={draft.rate_multiplier} onChange={(e) => setDraft({ ...draft, rate_multiplier: e.target.value })} /></FormField>
            </div>
            <FormField label="Reason"><Textarea value={draft.reason} onChange={(e) => setDraft({ ...draft, reason: e.target.value })} /></FormField>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button onClick={() => create.mutate()} disabled={!draft.employee_id || create.isPending}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
